import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { Table } from 'primeng/table';
import { ExportExcelService } from '../../../shared/excel-service/export-excel.service';
import { FiltroDealers } from './filtro-dealer';
import { RankingComprasFiltroService } from './ranking-compras-filtro.service';
import { RankingComprasCliente, RankingComprasDetalheItem } from './ranking-compras';
import { RankingComprasService } from './ranking-compras.service';

@Component({
  selector: 'app-ranking-compras',
  templateUrl: './ranking-compras.component.html',
  styleUrls: ['./ranking-compras.component.scss'],
})
export class RankingComprasComponent implements OnInit {
  @Input() filtros: any;

  form: FormGroup;
  subscription: any;
  cols: any[];
  colsDetalhe: any[];
  _selectedColumns: any[];
  clientesDados: RankingComprasCliente[] = [];
  comprasDados: RankingComprasDetalheItem[] = [];
  clienteSelecionado: RankingComprasCliente | null = null;
  loading = false;
  loadingDetalhe = false;
  displayFiltrosDealers = false;
  displayDetalhe = false;
  nomeModulo: string;
  barChartType = 'horizontalBar';
  barChartLegend = false;
  barChartLabels: string[] = [];
  barChartData: any[] = [];
  barChartOptions: any;
  clientesGrafico: RankingComprasCliente[] = [];
  chartHeight = 360;
  barChartColors: any[] = [{
    backgroundColor: 'rgba(0, 152, 223, 0.7)',
    borderColor: 'rgb(0, 152, 223)',
    hoverBackgroundColor: 'rgba(0, 152, 223, 0.9)',
    borderWidth: 1,
  }];
  dataInicial: Date;
  dataFinal: Date;
  maxDate: Date = new Date();
  filtrosDealers: FiltroDealers;
  cargaTela = true;
  userDealerIds = '';
  totalRegistros = 0;
  limite = 20;

  @ViewChild('dt') dt: Table;
  @ViewChild('dtDetalhe') dtDetalhe: Table;

  get dadosParaCalculo(): RankingComprasCliente[] {
    if (this.dt && this.dt.filteredValue) {
      return this.dt.filteredValue;
    }
    return this.clientesDados ?? [];
  }

  get totalQtd(): number {
    return this.dadosParaCalculo.reduce((acc, cur) => acc + (Number(cur.qtd) || 0), 0);
  }

  get totalValor(): number {
    return this.dadosParaCalculo.reduce((acc, cur) => acc + (Number(cur.total) || 0), 0);
  }

  get totalPct(): number {
    return this.dadosParaCalculo.reduce((acc, cur) => acc + (Number(cur.pct) || 0), 0);
  }

  get totalNotasDetalhe(): number {
    return this.comprasDados.reduce((acc, cur) => acc + (Number(cur.totalNota) || 0), 0);
  }

  constructor(
    private service: RankingComprasService,
    public translate: TranslateService,
    private servico: RankingComprasFiltroService,
    private exportExcelService: ExportExcelService,
    private fb: FormBuilder
  ) {
    this.filtros = {};
    this.nomeModulo = 'ranking-compras';
    this.filtrosDealers = new FiltroDealers(this.servico);
    this.tratarData();

    this.form = this.fb.group({
      data_inicial: [moment(this.dataInicial).format('YYYY-MM-DD')],
      data_final: [moment(this.dataFinal).format('YYYY-MM-DD')],
      dealer: [''],
    });
  }

  ngOnInit() {
    this.barChartOptions = this.criarOpcoesGrafico();
    this.changeLanguageValue();

    this.cols = [
      { field: 'posicao', header: 'businessIntelligence.rankingCompras.posicao', headerBr: 'Posição', headerGb: 'Position' },
      { field: 'documento', header: 'businessIntelligence.rankingCompras.documento', headerBr: 'CNPJ', headerGb: 'Tax ID', type: 'documento' },
      { field: 'nome', header: 'businessIntelligence.rankingCompras.nome', headerBr: 'Fornecedor', headerGb: 'Supplier' },
      { field: 'qtd', header: 'businessIntelligence.rankingCompras.qtd', headerBr: 'Qtd. Notas', headerGb: 'Invoice Qty' },
      { field: 'total', header: 'businessIntelligence.rankingCompras.total', headerBr: 'Total', headerGb: 'Total', type: 'currency' },
      { field: 'pct', header: 'businessIntelligence.rankingCompras.pct', headerBr: '%', headerGb: '%', type: 'percent' },
    ];

    this.colsDetalhe = [
      { field: 'codDealer', header: 'businessIntelligence.rankingCompras.codDealer', headerBr: 'Cód. Dealer', headerGb: 'Dealer Code' },
      { field: 'dealer', header: 'businessIntelligence.rankingCompras.dealer', headerBr: 'Dealer', headerGb: 'Dealer' },
      { field: 'documento', header: 'businessIntelligence.rankingCompras.documento', headerBr: 'CNPJ', headerGb: 'Tax ID', type: 'documento' },
      { field: 'nomeFornecedor', header: 'businessIntelligence.rankingCompras.nomeFornecedor', headerBr: 'Fornecedor', headerGb: 'Supplier' },
      { field: 'numeroNotaCompra', header: 'businessIntelligence.rankingCompras.numeroNotaCompra', headerBr: 'Nº Nota', headerGb: 'Invoice No.' },
      { field: 'serie', header: 'businessIntelligence.rankingCompras.serie', headerBr: 'Série', headerGb: 'Series' },
      { field: 'dataEmissao', header: 'businessIntelligence.rankingCompras.dataEmissao', headerBr: 'Data Emissão', headerGb: 'Issue Date', type: 'date' },
      { field: 'totalNota', header: 'businessIntelligence.rankingCompras.totalNota', headerBr: 'Valor Nota', headerGb: 'Invoice Amount', type: 'currency' },
    ];

    this._selectedColumns = this.cols;

    this.filtrosDealers.carregar().then(() => {
      this.userDealerIds = this.filtrosDealers.idsUsuario();
      this.getAll();
    });
  }

  @Input() get selectedColumns(): any[] {
    return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
    this._selectedColumns = this.cols.filter(col => val.includes(col));
  }

  get f() {
    return this.form.controls;
  }

  private getAll() {
    this.loading = true;
    this.clienteSelecionado = null;
    this.comprasDados = [];
    this.displayDetalhe = false;

    this.service
      .getRanking(
        this.f.data_inicial.value,
        this.f.data_final.value,
        this.userDealerIds,
        this.f.dealer.value || undefined,
        this.limite || 20
      )
      .subscribe(
        (response) => {
          this.clientesDados = response.clientes ?? [];
          this.totalRegistros = response.total_registros ?? this.clientesDados.length;
          this.atualizarGrafico();
          this.loading = false;
          this.cargaTela = false;
        },
        () => {
          this.clientesDados = [];
          this.totalRegistros = 0;
          this.limparGrafico();
          this.loading = false;
          this.cargaTela = false;
        }
      );
  }

  public detalharCliente(cliente: RankingComprasCliente) {
    this.clienteSelecionado = cliente;
    this.comprasDados = [];

    if (!cliente.qtd) {
      this.loadingDetalhe = false;
      this.displayDetalhe = false;
      return;
    }

    this.displayDetalhe = true;
    this.loadingDetalhe = true;

    this.service
      .getDetalhe(
        this.f.data_inicial.value,
        this.f.data_final.value,
        this.userDealerIds,
        this.f.dealer.value || undefined,
        this.isSemCliente(cliente) ? undefined : cliente.codigoCliente,
        this.isSemCliente(cliente) ? 'S' : undefined
      )
      .subscribe(
        (response) => {
          this.comprasDados = response.compras ?? [];
          this.loadingDetalhe = false;
        },
        () => {
          this.comprasDados = [];
          this.loadingDetalhe = false;
        }
      );
  }

  public fecharDetalhe() {
    this.displayDetalhe = false;
    this.clienteSelecionado = null;
    this.comprasDados = [];
  }

  public onChartClick(event: any) {
    const index = this.indiceBarraClicada(event);
    if (index != null && this.clientesGrafico[index]) {
      this.detalharCliente(this.clientesGrafico[index]);
    }
  }

  public filtroAPI() {
    this.f.data_inicial.setValue(moment(this.dataInicial).format('YYYY-MM-DD'));
    this.f.data_final.setValue(moment(this.dataFinal).format('YYYY-MM-DD'));
    this.filtrosSelecionados();
    this.getAll();
  }

  private filtrosSelecionados() {
    const selecionados = this.filtrosDealers.selecionados();
    if (selecionados.length > 0) {
      this.f.dealer.setValue(selecionados.map(d => d.id).join(','));
    } else {
      this.f.dealer.setValue('');
    }
  }

  public selecionarDealer() {
    this.displayFiltrosDealers = true;
  }

  public removerDealer(dealer: any) {
    dealer.selecionado = false;
    this.filtrosSelecionados();
  }

  public onAdicionarDealerFiltro(dealer: any) {
    dealer.selecionado = true;
    this.filtrosSelecionados();
  }

  public isSemCliente(cliente: RankingComprasCliente): boolean {
    return cliente?.codigoCliente == null
      && cliente?.documento == null
      && cliente?.nome == null;
  }

  public labelCliente(cliente: RankingComprasCliente): string {
    if (this.isSemCliente(cliente)) {
      return this.translate.instant('businessIntelligence.rankingCompras.semCliente');
    }
    return cliente?.nome ?? '—';
  }

  public formatarDocumento(documento: string | null): string {
    if (!documento || documento.length !== 14) {
      return documento ?? '—';
    }
    return documento.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  }

  public formatarDataEmissao(data: string): string {
    if (!data) {
      return '—';
    }
    const parsed = moment(data);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : data;
  }

  private tratarData() {
    const dataInicio = new Date();
    const dataFim = new Date();
    const firstDay = new Date(dataInicio.getFullYear(), dataInicio.getMonth(), 1);
    this.dataInicial = firstDay;
    this.dataFinal = dataFim;
  }

  public limpaDataInicial() {
    const dataInicio = new Date();
    this.dataInicial = new Date(dataInicio.getFullYear(), dataInicio.getMonth(), 1);
  }

  public limpaDataFinal() {
    const dataFim = new Date();
    this.dataFinal = new Date(dataFim.getFullYear(), dataFim.getMonth(), dataFim.getDate());
  }

  protected getTranslate(value: string) {
    return value;
  }

  private translateHeader(item: string): string {
    const col = this.cols.find(c => c.header === item) || this.colsDetalhe.find(c => c.header === item);
    if (!col) {
      return item;
    }
    return this.translate.currentLang === 'br' ? col.headerBr : col.headerGb;
  }

  public exportExcel() {
    if (!this.clientesDados?.length) {
      return;
    }

    this.exportExcelService.nomeModulo = this.nomeModulo;
    const exportDados = this.dadosParaCalculo.map(r => {
      const linha: Record<string, unknown> = {};
      this._selectedColumns.forEach(c => {
        if (c.field === 'nome') {
          linha[this.translateHeader(c.header)] = this.labelCliente(r);
        } else if (c.type === 'documento') {
          linha[this.translateHeader(c.header)] = this.formatarDocumento(r[c.field]);
        } else if (c.type === 'currency') {
          linha[this.translateHeader(c.header)] = r[c.field];
        } else if (c.type === 'percent') {
          linha[this.translateHeader(c.header)] = r[c.field];
        } else {
          linha[this.translateHeader(c.header)] = r[c.field];
        }
      });
      return linha;
    });

    const linhaTotal: Record<string, unknown> = {};
    const labelTotal = this.translate.currentLang === 'br' ? 'TOTAL' : 'TOTAL';
    this._selectedColumns.forEach(c => {
      const header = this.translateHeader(c.header);
      if (c.field === 'posicao') {
        linhaTotal[header] = labelTotal;
      } else if (c.field === 'qtd') {
        linhaTotal[header] = this.totalQtd;
      } else if (c.field === 'total') {
        linhaTotal[header] = this.totalValor;
      } else if (c.field === 'pct') {
        linhaTotal[header] = this.totalPct;
      } else {
        linhaTotal[header] = '';
      }
    });
    exportDados.push(linhaTotal);

    this.exportExcelService.ExportTOExcelJSON(exportDados);
  }

  public exportExcelDetalhe() {
    if (!this.comprasDados?.length || !this.clienteSelecionado) {
      return;
    }

    const codigo = this.isSemCliente(this.clienteSelecionado)
      ? 'sem_cliente'
      : String(this.clienteSelecionado.codigoCliente ?? '').replace(/[^\w.-]/g, '_') || 'cliente';
    this.exportExcelService.nomeModulo = `ranking_compras_${codigo}_${this.formatarDataArquivo()}`;

    this.exportExcelService.ExportTOExcelJSON(
      this.comprasDados.map(r => {
        const linha: Record<string, unknown> = {};
        this.colsDetalhe.forEach(c => {
          if (c.type === 'documento') {
            linha[this.translateHeader(c.header)] = this.formatarDocumento(r[c.field]);
          } else if (c.type === 'date') {
            linha[this.translateHeader(c.header)] = this.formatarDataEmissao(r[c.field]);
          } else {
            linha[this.translateHeader(c.header)] = r[c.field];
          }
        });
        return linha;
      })
    );
  }

  private formatarDataArquivo(): string {
    const hoje = new Date(Date.now());
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${hoje.getFullYear()}-${pad(hoje.getMonth() + 1)}-${pad(hoje.getDate())}`;
  }

  private changeLanguageValue() {
    this.subscription = this.translate.onLangChange.subscribe((_event: LangChangeEvent) => {
      if (this.clientesDados?.length) {
        this.getAll();
      }
    });
  }

  private atualizarGrafico() {
    this.clientesGrafico = [...this.clientesDados].sort((a, b) => a.posicao - b.posicao);

    this.barChartLabels = this.clientesGrafico.map(item => this.labelGrafico(item));
    this.barChartData = [{
      data: this.clientesGrafico.map(item => Number(item.qtd) || 0),
      label: this.translate.instant('businessIntelligence.rankingCompras.qtd'),
    }];
    this.chartHeight = Math.max(360, this.clientesGrafico.length * 44);
  }

  private labelGrafico(item: RankingComprasCliente): string {
    const nome = this.isSemCliente(item)
      ? this.translate.instant('businessIntelligence.rankingCompras.semCliente')
      : (item.nome ?? '');
    const nomeCurto = nome.length > 18 ? `${nome.substring(0, 18)}...` : nome;
    return `#${item.posicao} ${nomeCurto}`;
  }

  private limparGrafico() {
    this.clientesGrafico = [];
    this.barChartLabels = [];
    this.barChartData = [];
    this.chartHeight = 360;
  }

  private indiceBarraClicada(event: any): number {
    if (!event || !event.active || !event.active.length) {
      return null;
    }
    const ativo = event.active[0];
    if (ativo._index != null) {
      return ativo._index;
    }
    return ativo.index != null ? ativo.index : null;
  }

  private criarOpcoesGrafico(): any {
    const corPreta = '#000000';
    const component = this;
    const labelQtd = () => component.translate.instant('businessIntelligence.rankingCompras.qtd');

    return {
      responsive: true,
      maintainAspectRatio: false,
      legend: { display: false },
      scales: {
        xAxes: [{
          ticks: {
            beginAtZero: true,
            fontColor: corPreta,
            fontStyle: 'bold',
            callback(value: number) {
              return value.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
            },
          },
          gridLines: { color: 'rgba(0, 0, 0, 0.08)' },
        }],
        yAxes: [{
          ticks: {
            fontColor: corPreta,
            fontStyle: 'bold',
            autoSkip: false,
          },
          gridLines: { color: 'rgba(0, 0, 0, 0.08)' },
          reverse: true,
        }],
      },
      tooltips: {
        callbacks: {
          title(tooltipItems: any[]) {
            const index = tooltipItems[0]?.index;
            const cliente = component.clientesGrafico[index];
            if (!cliente) {
              return '';
            }
            return component.isSemCliente(cliente)
              ? component.translate.instant('businessIntelligence.rankingCompras.semCliente')
              : (cliente.nome ?? '');
          },
          label(tooltipItem: any, data: any) {
            const valor = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
            return `${labelQtd()}: ${Number(valor).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
          },
        },
      },
      onHover(event: MouseEvent, chartElement: any[]) {
        const target = event?.target as HTMLElement;
        if (target) {
          target.style.cursor = chartElement?.length ? 'pointer' : 'default';
        }
      },
    };
  }
}
